import { isNaN, isFinite } from 'lodash';

export class BaseMapAutoZoomService {
  constructor(topPercent = 100) {
    this.topPercent = topPercent; // take top X% points by field
  }

  // Parse raw data into {lon, lat, field}
  parseData(rawData) {
    return rawData
      .filter(
        (d) =>
          Array.isArray(d.coordinates) &&
          !isNaN(parseFloat(d.coordinates[0])) &&
          !isNaN(parseFloat(d.coordinates[1])) &&
          isFinite(parseFloat(d.coordinates[0])) &&
          isFinite(parseFloat(d.coordinates[1]))
      )
      .map((d) => ({
        lon: parseFloat(d.coordinates[0]),
        lat: parseFloat(d.coordinates[1]),
        field: parseInt(String(d.field).replace(/,/g, ''), 10),
      }));
  }

  // Get top X% of data points based on field value
  getTopPercent(parsedData) {
    // Sort descending by field
    const sorted = [...parsedData].sort((a, b) => b.field - a.field);
    // Compute how many to take
    const count = Math.ceil((this.topPercent / 100) * sorted.length);
    return sorted.slice(0, count);
  }

  // Compute bounds from points
  computeBounds(points) {
    const longs = points.map((d) => d.lon);
    const lats = points.map((d) => d.lat);

    return {
      west: Math.min(...longs),
      south: Math.min(...lats),
      east: Math.max(...longs),
      north: Math.max(...lats),
    };
  }

  normalizeBounds(bounds) {
    let { west, east, south, north } = bounds;

    // Clamp to valid ranges
    north = Math.min(90, north);
    south = Math.max(-90, south);

    // Handle Date Line crossing
    const span = east - west;
    if (span > 180) {
      // Crosses 180° — flip so that right < left (shorter arc)
      const newWest = east;
      const newEast = west + 360;
      west = newWest;
      east = newEast;
    }

    // Wrap into [-180, 180]
    if (west > 180) west -= 360;
    if (east > 180) east -= 360;

    // Make sure west < east for amCharts
    if (west > east) {
      const temp = west;
      west = east;
      east = temp;
    }

    // Detect anti-meridian crossing
    if (east < west) {
      // Add 360° to right to bring it into correct range
      east += 360;
    }

    // Optional: Clamp to valid world range
    const left = Math.max(-180, west);
    const right = Math.min(180, east);
    const top = Math.min(85, north);
    const bottom = Math.max(-85, south);

    return { left, right, top, bottom };
  }

  // Main method: auto-zoom based on top % values
  getBounds(rawData) {
    if (rawData.length === 0) return [];

    let parsedData = this.parseData(rawData);
    let topPoints = this.getTopPercent(parsedData);
    if (topPoints.length === 0) return [];
    let computedBounds = this.computeBounds(topPoints);
    return this.normalizeBounds(computedBounds);
  }
}
